import { CreateLabelText, GetTextStyle } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/menu/AbstractScrollableContentView";
import { BasePaytableComponent } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/menu/paytable/components/BasePaytableComponent";
import { alignSymbolEntries } from "@westpierstudio/core-module-oryx/components/ui/slot-game-ui/menu/paytable/SymbolsContentView";
import { PaytableContentFactoryService } from "@westpierstudio/core-module-oryx/services";
import { OpenGameEvent, PayRow } from "servertypes/gameevents/OpenGame";
import { GameServices } from "../GameServices";
import {Container, Sprite} from "pixi.js";
import { uniq, last, initial } from "lodash";
import { SpriteAssetSpec } from "@westpierstudio/luma/factory/PixiFactory";
import { maxWinConfigsByLineCount } from "./MaxWinConfigs";

export class LuckyWealthCatContentFactoryService extends PaytableContentFactoryService {

    paylines?: PayRow[][];

    override createComponent(componentName: string, payload: unknown, services: GameServices, createLabelText: CreateLabelText, getTextStyle: GetTextStyle) {
        switch (componentName) {
            case `paylinesContainer`:
                return this.createPaylinesContainerComponent(services, <PaylinesContainerPayload>payload);
            case `paylinesConditionalSprite`:
                return this.createPaylinesConditionalSpriteComponent(services, <PaylinesConditionalSpritePayload>payload);
            default:
                return super.createComponent(componentName, payload, services, createLabelText, getTextStyle);
        }
    }

    setPayLines(paylines: PayRow[][]) {
        this.paylines = paylines;
    }

    createPaylinesContainerComponent(services: GameServices, payload: PaylinesContainerPayload) {
        if (!this.paylines) {
            return undefined;
        }
        return new PaylinesContentContainerComponent(services, payload, this.paylines);
    }

    createPaylinesConditionalSpriteComponent(services: GameServices, payload: PaylinesConditionalSpritePayload) {
        if (!this.paylines) {
            return undefined;
        }
        return new PaylinesConditionalSpriteComponent(services, payload, this.paylines);
    }

    setOpenGameData(services: GameServices, event: OpenGameEvent, winLineCount: number) {

        const multiplierFormat: string = services.resources.getSpec(`ui`).paytable.multiplierFormat;
        const multiplierJoin: string = services.resources.getSpec(`ui`).paytable.multiplierJoin;

        const byValueAsc = (a: number, b: number) => a - b;
        const allBaseGameMultiplierValues = uniq(Object.values(event.body.data.symbolMultipliers.INITIAL_SPIN)).sort(byValueAsc);
        const allFreeSpinMultiplierValues = uniq(Object.values(event.body.data.symbolMultipliers.FREE_SPIN)).sort(byValueAsc);
        const allMultiplierValues = uniq([...allBaseGameMultiplierValues, ...allFreeSpinMultiplierValues]).sort(byValueAsc);

        const maxBaseGameMultiplier = last(allBaseGameMultiplierValues)!;
        const maxFreeSpinMultiplier = last(allFreeSpinMultiplierValues)!;
        const maxAllMultiplier = last(allMultiplierValues)!;

        const asMultiplier = (value: number) => multiplierFormat.replace(`{value}`, value.toString());
        this.setToken(`numberOfLines`, winLineCount.toString());
        this.setToken(`maxBaseGameMultiplier`, asMultiplier(maxBaseGameMultiplier));

        this.setToken(`lowerFreeGamesMultipliers`, initial(allFreeSpinMultiplierValues).map(asMultiplier).join(multiplierJoin));
        this.setToken(`maxFreeGamesMultiplier`, asMultiplier(maxFreeSpinMultiplier));

        this.setToken(`lowerAllMultipliers`, initial(allMultiplierValues).map(asMultiplier).join(multiplierJoin));
        this.setToken(`maxAllMultiplier`, asMultiplier(maxAllMultiplier));

        this.setToken(`multiplierDuration`, `${event.body.data.initialMultipliersCounterValue}`);

        this.setPayLines(event.body.data.paylinesRows);

        this.updateMaxWinConfigs(winLineCount);
    }

    updateMaxWinConfigs(winLineCount: number) {
        const rtpConfigs = maxWinConfigsByLineCount[winLineCount];
        if (!rtpConfigs) {
            console.warn(`No max-win rtp configs found for win-line-count ${winLineCount}`);
            return;
        }

        this.setMaxWinConfigs(rtpConfigs);
    }

}

type PaylinesContainerPayload = {
    textureName: string
}

class PaylinesContentContainerComponent extends BasePaytableComponent {

    constructor(services: GameServices, payload: PaylinesContainerPayload, paylines: PayRow[][]) {
        super(services);

        paylines.forEach((_, index) => {
            const sprite = services.factory.createSpriteFromTextureKey(`${payload.textureName}${index + 1}`, {}, this);
            this.addChild(sprite);
        });
    }

    onOpen(){}

    updateLayout(width: number, height: number): void {
        alignSymbolEntries(this as unknown as Container<Sprite>, width);
    }
  }


type PaylinesConditionalSpritePayload = {
    lines: Record<number, SpriteAssetSpec>
}

class PaylinesConditionalSpriteComponent extends BasePaytableComponent {

    constructor(services: GameServices, payload: PaylinesConditionalSpritePayload, paylines: PayRow[][]) {
        super(services);

        const numberofPayLines = paylines.length;
        this.addChild(services.factory.createSprite(payload.lines[numberofPayLines]), this);
    }

    onOpen(){}

    updateLayout(width: number, height: number): void {
        alignSymbolEntries(this as unknown as Container<Sprite>, width);
    }
  }